import * as React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { keyframes } from 'typestyle'
import { H3, EllipsisDiv, IntroText } from '../../../common/src/components/core'
import { Colors } from '../../../common/src/constants'
import { getWindow } from '../../../common/src/global'
import { createWidget } from './base'

const errorPageWidgetId = 'error-page'
const errorPage = createWidget(errorPageWidgetId, (props: ErrorPageProps) => <ErrorPage {...props} />)

/**
 * Animation constants for fade / rotate effects
 */
const secsDurationFadeIn = 1
const secsBeforeCountdownStarts = 8
const countdownDurationInSeconds = 12

interface ErrorPageProps {
  // the path of the requested resource that caused this error page to render.
  // eg, trying to load a transcript that wasn't there should result in this page.
  originalPath?: string | null
}

interface ErrorPageState {
  /** headline describes error or current countdown */
  largeText: string
  /** false, until you want to show a countdown in text */
  textCountdownStarted: boolean
}

class ErrorPage extends React.PureComponent<ErrorPageProps, ErrorPageState> {
  /**
   * Keep a handy ref for animating the "circle" part of the SVG
   */
  private circleRef = React.createRef<SVGCircleElement>()

  /**
   * When the page first loads, queue the countdown animation
   */
  state = {
    largeText: "Sorry, we couldn't find…",
    textCountdownStarted: false,
  }

  componentDidMount() {
    this.initCountdown()
  }

  /**
   * Animates two changes:
   * 1. warn the user that we're going "back"
   * 2. elapse the "countdown" circle like a stopwatch
   */
  initCountdown() {
    setTimeout(() => {
      this.changeLargeErrorMessage()
      this.startCircleSpinner()
      this.startTextCountdown()
    }, secsBeforeCountdownStarts * 1000)
  }

  startTextCountdown() {
    this.setState({
      textCountdownStarted: true,
    })
  }

  /**
   * Warn the user we're sending their browser "back"
   */
  changeLargeErrorMessage() {
    this.setState({
      largeText: 'Taking you back',
    })
  }

  /**
   * Animate the circle to imitate a stopwatch: slowly erase the circumference.
   */
  startCircleSpinner() {
    const circle = this.circleRef.current
    const radius = 36
    const circleLength = radius * 2 * Math.PI + 'px'

    if (circle) {
      circle.setAttribute('r', radius.toString())
      circle.style.strokeDasharray = circleLength
      circle.style.strokeDashoffset = circleLength

      const animation = circle.animate([{ strokeDashoffset: 0 }, { strokeDashoffset: circleLength }], {
        duration: countdownDurationInSeconds * 1000,
        easing: 'linear',
        iterations: 1,
        fill: 'forwards',
      })

      animation.onfinish = animation.oncancel = () => {
        console.log('countdown complete, going back ...')
        getWindow()!.history.back()
      }
    }
  }

  render() {
    return (
      <ErrorPageWrapper>
        <CountdownWrapper viewBox="-40 -40 80 80">
          <CountdownCircle ref={this.circleRef} r="36" />
        </CountdownWrapper>
        <ErrorDescription>
          <H3>{this.state.largeText}</H3>
          <EllipsisDiv>
            {!this.state.textCountdownStarted && <IntroText>{this.props.originalPath}</IntroText>}
            {this.state.textCountdownStarted && <CountDown duration={countdownDurationInSeconds} />}
          </EllipsisDiv>
        </ErrorDescription>
      </ErrorPageWrapper>
    )
  }
}

function CountDown({ duration }: { duration: number }) {
  const [remainingTime, setRemainingTime] = useState(duration)

  useEffect(() => {
    setTimeout(() => {
      if (remainingTime === 0) {
        return
      }

      setRemainingTime(remainingTime - 1)
    }, 1000)
  })

  return <IntroText>in {remainingTime}</IntroText>
}

const ErrorPageWrapper = styled.div({
  backgroundColor: Colors.ink,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: Colors.white,
})

/**
 * Fade-in an element with a subtle "drop-in" translate-Y effect
 */
const fadeIn = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(-4px)',
  },
  to: {
    opacity: 1,
    transform: 'translateY(0px)',
  },
})

/**
 * Rotate an element a full 360˚
 */
const rotate = keyframes({
  from: {
    transform: 'rotate(360deg)',
  },
  to: {
    transform: 'rotate(0deg)',
  },
})

const ErrorDescription = styled.div({
  width: '50%',
  minWidth: '320px',
  textAlign: 'center',
  animation: `${fadeIn} ${secsDurationFadeIn}s ease`,
  position: 'relative',
  zIndex: 2,
})

const CountdownWrapper = styled.svg({
  position: 'absolute',
  zIndex: 1,
  height: '500px',
  width: '500px',
  left: '50%',
  marginLeft: '-250px',
  top: '50%',
  marginTop: '-250px',
  transform: 'rotate(270deg)' /* start notch at top of circle */,
})

const CountdownCircle = styled.circle({
  strokeLinecap: 'round',
  strokeWidth: 0.25,
  stroke: Colors.steel,
  fill: 'none',
  animation: `${rotate} ${countdownDurationInSeconds}s infinite linear`,
  animationDelay: `${secsBeforeCountdownStarts}s`,
})

export function getErrorPageHTML(originalPath?: string | null) {
  return errorPage.renderHtml('error page title', { originalPath })
}

export function hydrateErrorPageWidget() {
  // Only hydrate widget if it's actually rendered
  if (document.getElementById(errorPageWidgetId) == null) {
    return
  }

  errorPage.init({ originalPath: location.pathname + location.search })
}
